import type { OrganisationRecordingSetting } from "./access.ts";

export type RecordingTarget =
  | { kind: "organisation" }
  | { kind: "department"; departmentId: string }
  | { kind: "tag"; key: string; value: string };

export type RecordingPolicyRow = {
  key: string;
  name: string;
  kind: string;
  recordingLabel: string;
  stealthLabel: string;
  effectiveLabel: string;
  organisationId: string;
  target: RecordingTarget;
};

function ruleWord(rule: string): string {
  return rule === "Allowed" ? "Optional" : rule;
}

function recordingChoiceLabel(choice: string, effective: string, locked: boolean): string {
  if (locked) return "required by organisation";
  return `${ruleWord(choice)} (${ruleWord(effective)})`;
}

export function flattenRecordingPolicyRows(rows: OrganisationRecordingSetting[]): RecordingPolicyRow[] {
  const flat: RecordingPolicyRow[] = [];
  for (const org of rows) {
    flat.push({
      key: org.organisationId,
      name: org.name,
      kind: "Organisation",
      recordingLabel: org.recordingRule,
      stealthLabel: org.stealthCommand ? "On" : "Off",
      effectiveLabel: ruleWord(org.recordingRule),
      organisationId: org.organisationId,
      target: { kind: "organisation" },
    });
    for (const department of org.departments) {
      flat.push({
        key: department.departmentId,
        name: department.name,
        kind: "Department",
        recordingLabel: department.locked ? "Required" : department.recordingRule,
        stealthLabel: department.stealthCommand ? "On" : "Off",
        effectiveLabel: recordingChoiceLabel(department.recordingRule, department.effectiveRule, department.locked),
        organisationId: org.organisationId,
        target: { kind: "department", departmentId: department.departmentId },
      });
    }
    for (const tag of org.tags) {
      const name = tag.value.length > 0 ? `${tag.key}:${tag.value}` : tag.key;
      flat.push({
        key: `${tag.key}:${tag.value}`,
        name,
        kind: "Tag",
        recordingLabel: tag.locked ? "Required" : tag.recordingRule,
        stealthLabel: tag.stealthCommand ? "On" : "Off",
        effectiveLabel: recordingChoiceLabel(tag.recordingRule, tag.effectiveRule, tag.locked),
        organisationId: org.organisationId,
        target: { kind: "tag", key: tag.key, value: tag.value },
      });
    }
  }
  return flat;
}
